import React, { useCallback, useState } from 'react';
import { Alert, ScrollView, StyleSheet, Text, View } from 'react-native';
import {
  Button,
  Switch as PaperSwitch,
  TextInput as PaperTextInput,
  useTheme,
} from 'react-native-paper';
import BaseBottomSheet from './BaseBottomSheet';
import { useAuth } from '../context/AuthContext';
import TruckService from '../services/truckService';
import { CreateTruckData } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';

export interface TruckData {
  plateNumber: string;
  model?: string;
  color?: string;
  isActive: boolean;
}

interface AddTruckBottomSheetProps {
  visible: boolean;
  onSave?: (truck: TruckData) => void; // Optional callback for external handling
  onClose: () => void;
  onTruckCreated?: () => void; // Callback when truck is successfully created
  onSuccess?: () => void; // Callback to show success message
}

const AddTruckBottomSheet: React.FC<AddTruckBottomSheetProps> = ({
  visible,
  onSave,
  onClose,
  onTruckCreated,
  onSuccess,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [plateNumber, setPlateNumber] = useState('');
  const [model, setModel] = useState('');
  const [color, setColor] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!plateNumber.trim()) {
      Alert.alert('Error', 'Please enter a plate number');
      return;
    }

    if (!user?.uid) {
      Alert.alert('Error', 'You must be logged in to add a truck');
      return;
    }

    try {
      setSaving(true);

      const truckData: CreateTruckData = {
        plateNumber: plateNumber.trim(),
        model: model.trim() || undefined,
        color: color.trim() || undefined,
        isActive,
      };

      // Save to Firestore
      await TruckService.createTruck(user.uid, truckData);

      // Call legacy callback if provided (for backward compatibility)
      onSave?.({
        plateNumber: plateNumber.trim(),
        model: model.trim(),
        color: color.trim(),
        isActive,
      });

      // Reset form
      setPlateNumber('');
      setModel('');
      setColor('');
      setIsActive(true);

      // Notify parent component
      onTruckCreated?.();

      // Close the modal first
      onClose();

      // Show success message
      onSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'AddTruckBottomSheet.handleSave');
      Alert.alert('Error', 'Failed to save truck. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    plateNumber,
    model,
    color,
    isActive,
    user,
    onSave,
    onTruckCreated,
    onClose,
    onSuccess,
  ]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <BaseBottomSheet
      visible={visible}
      onClose={handleClose}
      title="Add Truck"
      height={0.7}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Plate Number Field */}
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Plate Number *"
            value={plateNumber}
            onChangeText={setPlateNumber}
            placeholder="e.g., UBG 123A"
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="plate-input"
          />
        </View>

        {/* Model Field */}
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Model"
            value={model}
            onChangeText={setModel}
            placeholder="e.g., Isuzu NQR, Fuso Canter"
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="model-input"
          />
        </View>

        {/* Color Field */}
        <View style={styles.fieldContainer}>
          <PaperTextInput
            mode="flat"
            label="Color"
            value={color}
            onChangeText={setColor}
            placeholder="e.g., White, Blue, Red"
            style={[styles.paperInput, { backgroundColor: theme.colors.surfaceVariant }]}
            testID="color-input"
          />
        </View>

        {/* Active Status */}
        <View style={styles.fieldContainer}>
          <View style={[styles.switchContainer, { backgroundColor: theme.colors.surfaceVariant }]}>
            <View>
              <Text
                style={[styles.switchLabel, { color: theme.colors.onSurface }]}
              >
                Active Truck
              </Text>
              <Text
                style={[
                  styles.switchDescription,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Active trucks can be assigned to trips and expenses
              </Text>
            </View>
            <PaperSwitch
              value={isActive}
              onValueChange={setIsActive}
              testID="active-switch"
            />
          </View>
        </View>
      </ScrollView>

      {/* Save Button */}
      <View style={styles.buttonContainer}>
        <Button
          disabled={!plateNumber.trim() || saving}
          mode="contained"
          onPress={handleSave}
          loading={saving}
          testID="save-button"
        >
          {saving ? 'Saving...' : 'Add Truck'}
        </Button>
      </View>
    </BaseBottomSheet>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flexShrink: 1,
  },
  scrollContent: {
    padding: 20,
  },
  fieldContainer: {
    marginBottom: 16,
  },
  paperInput: {},
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 6,
    padding: 16,
  },
  switchLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 16,
    marginBottom: 2,
  },
  switchDescription: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    lineHeight: 16,
  },
  buttonContainer: {
    padding: 16,
    paddingBottom: 16,
  },
});

export default AddTruckBottomSheet;
